/*
================================================================================
File: src/pages/SellerResponders.js
Description: Displays and manages autoresponder sequences for seller leads.
             Includes components for viewing, creating, and editing sequences.
================================================================================
*/
import React, { useState, useEffect, useCallback, useRef } from "react";
import Select from "react-tailwindcss-select";
import instance from "../../api/axios";
import { storage } from "../../App";
import PageContent from "../../components/common/PageContent";
import Modal from "../../components/common/Modal";
import { seq_options, shortcuts } from "../../constants/options";
import { convertSecondsToMessage } from "../../utils/formatters";
import { useAdaptiveMMKV } from "../../hooks/useAdaptiveMMKV";

// --- Reusable Modal for Editing/Creating a Sequence ---
const SequenceModal = ({
  isOpen,
  onClose,
  onSave,
  sequenceData,
  user,
  type,
  isNew,
  shortname,
  setError,
}) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [responderName, setResponderName] = useState(shortname || "");
  const [sequenceTime, setSequenceTime] = useState({
    value: 0,
    label: "Immediately",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [activeInput, setActiveInput] = useState(null);
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  useEffect(() => {
    if (isOpen) {
      if (isNew) {
        setSubject("");
        setBody("");
        setResponderName(shortname || "");
        setSequenceTime({ value: 0, label: "Immediately" });
      } else if (sequenceData) {
        setSubject(sequenceData.subject || "");
        setBody(sequenceData.body || "");
        setSequenceTime(
          seq_options.find(
            (opt) => opt.value === parseInt(sequenceData.sequence_time)
          ) || { value: 0, label: "Immediately" }
        );
      }
    }
  }, [isOpen, isNew, sequenceData, shortname]);

  const handleSave = async () => {
    setIsSaving(true);
    const formData = new FormData();
    formData.append("owner", user.app_id);
    formData.append("type", "3");

    let url = "";
    if (isNew) {
      url = "/add_responder.php";
      formData.append("short_name_add", responderName);
      formData.append("subject_add", subject);
      formData.append("body_add", body);
      formData.append("seq_time_add", sequenceTime.value);
    } else {
      url = "/updateResponder.php";
      formData.append("shortname", responderName);
      formData.append("pid", sequenceData.id);
      formData.append("body", body);
      formData.append("subject", subject);
      formData.append("seq_time", sequenceTime.value);
    }
    try {
      await instance.post(url, formData);
      setError(null);

      onSave();
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError("Error saving sequence: " + e.response.data.message);
        console.error("Error saving sequence: ", e.response.data.message);
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
      setIsSaving(false);
      onClose();
    }
  };

  const handleShortcutClick = (shortcut) => {
    const textToInsert = `{${shortcut}}`;
    if (activeInput === "subject" && subjectRef.current) {
      const { selectionStart, selectionEnd } = subjectRef.current;
      const newText =
        subject.substring(0, selectionStart) +
        textToInsert +
        subject.substring(selectionEnd);
      setSubject(newText);
    } else if (activeInput === "body" && bodyRef.current) {
      const { selectionStart, selectionEnd } = bodyRef.current;
      const newText =
        body.substring(0, selectionStart) +
        textToInsert +
        body.substring(selectionEnd);
      setBody(newText);
    }
  };

  const footer = (
    <button
      onClick={handleSave}
      disabled={isSaving}
      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700"
    >
      {isSaving ? "Saving..." : "Save Changes"}
    </button>
  );

  return (
    <Modal
      show={isOpen}
      onClose={onClose}
      title={isNew ? "Create Auto-Responder" : "Edit Auto-Responder Sequence"}
      footer={footer}
      maxWidth="max-w-6xl"
      icon={
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
        </svg>
      }
      iconColor="blue"
    >
      <div className="space-y-4 ">
        {isNew && !shortname && (
          <div>
            <label className="block text-sm font-medium text-gray-400 dark:bg-gray-800 dark:text-gray-200">
              Autoresponder Title:
            </label>
            <input
              type="text"
              value={responderName}
              onChange={(e) => setResponderName(e.target.value)}
              onFocus={() => setActiveInput(null)}
              className="w-full p-2 border rounded-md dark:bg-gray-900 dark:text-gray-200"
            />
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-200">
            Sequence Frequency:
          </label>
          <Select
            value={sequenceTime}
            onChange={setSequenceTime}
            options={seq_options}
            classNames={{
    menuButton: ({ isDisabled }) =>
      `flex text-sm text-gray-700 border border-gray-300 rounded shadow-sm transition-all duration-300 focus:outline-none bg-white hover:border-gray-400 focus:border-indigo-500 focus:ring focus:ring-indigo-500/20 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200 ${
        isDisabled ? "bg-gray-200" : ""
      }`,
    menu: "absolute z-10 w-full bg-white shadow-lg border rounded py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200",
    listItem: ({ isSelected }) =>
      `block transition duration-200 px-2 py-2 cursor-pointer select-none truncate rounded ${
        isSelected
          ? "text-white bg-indigo-500"
          : "text-gray-500 hover:bg-indigo-100 hover:text-indigo-500 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white"
      }`,
  }}

          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-200">
            Email Subject:
          </label>
          <input
            ref={subjectRef}
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            onFocus={() => setActiveInput("subject")}
            className="w-full p-2 border rounded-md dark:bg-gray-900 dark:text-gray-200"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-200">
            Email Body:
          </label>
          <textarea
            ref={bodyRef}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onFocus={() => setActiveInput("body")}
            rows="12"
            className="w-full p-2 border rounded-md dark:bg-gray-800 dark:text-gray-200"
          ></textarea>
        </div>
        <div>
          <p className="text-sm font-medium text-blue-600 dark:bg-gray-800 dark:text-gray-200">
            DON'T FORGET TO AUTO-POPULATE YOUR INFORMATION
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            {shortcuts.map((shortcut) => (
              <button
                key={shortcut}
                type="button"
                onClick={() => handleShortcutClick(shortcut)}
                className="bg-gray-200 text-gray-700 text-xs font-semibold px-3 py-1 rounded-full hover:bg-gray-300 dark:bg-gray-900 dark:text-gray-200"
              >
                {`{${shortcut}}`}
              </button>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
};

// --- Component for Viewing Sequences within a Group ---
export const InvestorRespondersView = ({ onViewChange, shortname, user }) => {
  const [sequences, setSequences] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [error, setError] = useState(null);
  const [modalAction, setModalAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const menuRef = useRef([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside of any of the menu containers
      const activeRefs = menuRef.current.filter(ref => ref);
      const isOutside = activeRefs.length === 0 || activeRefs.every(
        (ref) => !ref.contains(event.target)
      );
      if (isOutside) {
        setMenuOpenFor(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const pullSequences = useCallback(async () => {
    const formData = new FormData();
    formData.append("owner", user.app_id);
    formData.append("shortname", shortname);
    try {
      const response = await instance.post("/investorResponders.php", formData);
      const sorted = [...response.data].sort((a, b) => a.sequence - b.sequence);
      setSequences(sorted);
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError("Error fetching sequences: " + e.response.data.message);
        console.error("Error fetching sequences: ", e.response.data.message);
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    }
  }, [user.app_id, shortname]);

  useEffect(() => {
    pullSequences();
  }, [pullSequences]);

  const renderModalContent = () => {
    return `Are you sure you want to delete the auto-responder sequence "${selectedSequence?.short_name}"?`;
  };

  const openModal = (action, item) => {
    setModalAction(action);
    setSelectedSequence(item);
  };

  const renderModalFooter = () => {
    const actionButton = (
      <button
        onClick={() => handleDelete(selectedSequence)}
        disabled={loading}
        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
      >
        {loading ? "Deleting..." : "Delete"}
      </button>
    );
    return <>{actionButton}</>;
  };

  const handleDelete = async (sequence) => {
    if (!sequence) return;
    setLoading(true);
    const formData = new FormData();
    formData.append("owner", user.app_id);
    formData.append("type", "3");
    formData.append("ids", sequence?.id || "");
    formData.append("seq", sequence?.sequence || "");
    formData.append("seq2", sequence?.sequence_sequence || "");
    formData.append("shortname", sequence?.short_name || "");

    try {
      await instance.post("/delete_sequence.php", formData);
      setError(null);
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError("Error deleting sequence: " + e.response.data.message);
        console.error("Error deleting sequence: ", e.response.data.message);
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
      await pullSequences();
      setLoading(false);
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sequences.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sequences.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageButtons = 10;
    const halfMax = Math.floor(maxPageButtons / 2);
    let startPage = Math.max(1, currentPage - halfMax);
    let endPage = Math.min(totalPages, currentPage + halfMax);

    if (currentPage <= halfMax) {
      endPage = Math.min(totalPages, maxPageButtons);
    }
    if (currentPage + halfMax > totalPages) {
      startPage = Math.max(1, totalPages - maxPageButtons + 1);
    }

    if (startPage > 1) {
      pageNumbers.push(
        <span key="ellipsis-start" className="px-2">
          ...
        </span>
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => paginate(i)}
          className={`
            w-8 h-8 rounded-full text-sm font-medium mx-1 transition-colors duration-200
            ${
              currentPage === i
                ? "bg-blue-600 text-white shadow-md"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }
          `}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      pageNumbers.push(
        <span key="ellipsis-end" className="px-2">
          ...
        </span>
      );
    }

    return pageNumbers;
  };

  return (
    <PageContent
      title="Investor Leads"
      subtitle="Autoresponders"
      thirdtitle={shortname}
      error={error}
    >
            <div className="mx-8 mt-8 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-10 rounded-xl shadow-lg mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-lg">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3.196 12.87c-.263.13-.597-.04-.686-.3a.5.5 0 00-.493-.39H1.5a.5.5 0 00-.5.5v.5c0 .278.224.5.5.5h.327c.077-.26.22-.5.414-.696M16.5 1H3.5A2.5 2.5 0 001 3.5v13A2.5 2.5 0 003.5 19h13a2.5 2.5 0 002.5-2.5v-13A2.5 2.5 0 0016.5 1zm-5 16.5h-3v-5h3v5zm5-7.5a.5.5 0 01-.5.5H4a.5.5 0 01-.5-.5v-5a.5.5 0 01.5-.5h12a.5.5 0 01.5.5v5z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold">Autoresponder Emails</h2>
                <p className="text-blue-100">Create and manage automated email campaigns</p>
              </div>
            </div>
            <div className="text-right text-blue-100 text-sm">
              <div className="font-semibold">{currentItems?.length || 0} Total Emails</div>
            </div>
          </div>
        </div>
      <div className="bg-white m-10 rounded-lg shadow-md dark:border dark:border-gray-700">
        <div className="p-4 flex justify-between items-center bg-gray-50 rounded-t-lg dark:text-gray-200 dark:bg-gray-800 ">
          <button
            onClick={() => onViewChange("investorresponders")}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center space-x-2 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 1024 1024"
              fill="#fff"
              className="icon"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M669.6 849.6c8.8 8 22.4 7.2 30.4-1.6s7.2-22.4-1.6-30.4l-309.6-280c-8-7.2-8-17.6 0-24.8l309.6-270.4c8.8-8 9.6-21.6 2.4-30.4-8-8.8-21.6-9.6-30.4-2.4L360.8 480.8c-27.2 24-28 64-0.8 88.8l309.6 280z"
                fill=""
              />
            </svg>{" "}
            <span>Go Back</span>
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700"
          >
            Create Sequence
          </button>
        </div>
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="overflow-y-visible">
          <table className="w-full text-sm text-left ">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:text-gray-200 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3">Subject</th>
                <th className="px-6 py-3">Sequence Time</th>
                <th className="px-6 py-4 text-center text-gray-600 dark:text-gray-400">Actionss</th>

              </tr>
            </thead>
            <tbody>
              {currentItems.map((item, index) => (
                <tr
                  key={index}
                  className="bg-white border-b hover:bg-gray-50 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900"
                >
                  <td className="px-6 py-4">{item.subject}</td>
                  <td className="px-6 py-4">
                    {convertSecondsToMessage(item.sequence_time)}
                  </td>
                  <td
                        className="px-6 py-4 text-center relative"
                    ref={(el) => (menuRef.current[index] = el)}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpenFor(item.id);
                      }}
                                                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"

                    >
                                                                                              <svg
                          className="w-5 h-5 text-gray-500 dark:text-gray-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                        </svg>
                    </button>
                    {menuOpenFor === item.id && (
                        <div className="absolute right-0 mt-2 w-48 bg-white border rounded shadow-lg z-10 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900">
                        <button
                          key={"0"}
                          onClick={() => {
                            setSelectedSequence(item);
                            setIsEditModalOpen(true);
                            setMenuOpenFor(null);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900"
                        >
                          Edit
                        </button>
                        <button
                          key={"1"}
                          onClick={() => openModal("delete", item)}
                          className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div></div>
        {sequences.length > itemsPerPage && (
          <div className="p-4 flex items-center justify-between border-t border-gray-200 bg-gray-50 rounded-b-2xl">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 text-sm font-medium rounded-lg text-gray-700 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <div className="flex-grow flex justify-center">
              {renderPageNumbers()}
            </div>
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 text-sm font-medium rounded-lg text-gray-700 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>

      <Modal
        show={modalAction}
        onClose={() => setModalAction(false)}
        title={"Delete Autoresponder Sequence"}
        footer={renderModalFooter()}
        icon={
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        }
        iconColor="red"
      >
        {renderModalContent()}
      </Modal>
      <SequenceModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={pullSequences}
        sequenceData={selectedSequence}
        user={user}
        type="3"
        isNew={false}
      />
      <SequenceModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={pullSequences}
        user={user}
        type="3"
        isNew={true}
        shortname={shortname}
      />
    </PageContent>
  );
};

// --- Main Component for Listing Auto-Responder Groups ---
const InvestorResponders = ({ onViewChange, user }) => {
  const [responders] = useAdaptiveMMKV(
    "user.Feed.Investor.Responders.All",
    storage
  );
  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 10;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [newName, setNewName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const menuRef = useRef([]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click is outside of any of the menu containers
      const activeRefs = menuRef.current.filter(ref => ref);
      const isOutside = activeRefs.length === 0 || activeRefs.every(
        (ref) => !ref.contains(event.target)
      );
      if (isOutside) {
        setMenuOpenFor(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const pullResponderData = useCallback(async () => {
    setLoading(true);
    const formData = new FormData();
    formData.append("owner", user.app_id);
    try {
      const response = await instance.post("/investorResponders.php", formData);
      const sortedData = [...response.data].sort((a, b) =>
        String(a.short_name).localeCompare(String(b.short_name))
      );
      storage.set(
        "user.Feed.Investor.Responders.All",
        JSON.stringify(sortedData)
      );

      setError(null);
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError(
          "Error fetching autoresponder data: " + e.response.data.message
        );
        console.error(
          "Error fetching autoresponder data: ",
          e.response.data.message
        );
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
      setLoading(false);
    }
  }, [user.app_id]);

  useEffect(() => {
    pullResponderData();
  }, [pullResponderData]);

  const handleAction = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData();
    formData.append("owner", user.app_id);
    formData.append("type", "3");
    formData.append("shortname", selectedItem.short_name);

    let url = "";
    if (modalAction === "delete") {
      url = "/delete_responder.php";
    } else if (modalAction === "rename") {
      url = "/edit_responder.php";
      formData.append("newName", newName);
    }
    formData.append("seq", selectedItem.sequence);

    try {
      await instance.post(url, formData);
      setError(null);
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError(
          `Error with action ${modalAction}: ` + e.response.data.message
        );
        console.error(
          `Error with action ${modalAction}: `,
          e.response.data.message
        );
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
      try {
        await pullResponderData();
      } catch (fetchError) {
        console.error("Error refetching responder data:", fetchError);
        setError("Error refreshing data: " + (fetchError.response?.data?.message || fetchError.message));
      }
      setLoading(false);
      setIsModalOpen(false);
    }
  };

  const openModal = (action, item) => {
    setModalAction(action);
    setSelectedItem(item);
    setNewName(action === "rename" ? item.short_name : "");
    setIsModalOpen(true);
    setMenuOpenFor(null);
  };

  const filteredResponders =
    responders?.filter((item) =>
      String(item.short_name).toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredResponders.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredResponders.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageButtons = 10;
    const halfMax = Math.floor(maxPageButtons / 2);
    let startPage = Math.max(1, currentPage - halfMax);
    let endPage = Math.min(totalPages, currentPage + halfMax);

    if (currentPage <= halfMax) {
      endPage = Math.min(totalPages, maxPageButtons);
    }
    if (currentPage + halfMax > totalPages) {
      startPage = Math.max(1, totalPages - maxPageButtons + 1);
    }

    if (startPage > 1) {
      pageNumbers.push(
        <span key="ellipsis-start" className="px-2">
          ...
        </span>
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => paginate(i)}
          className={`
            w-8 h-8 rounded-full text-sm font-medium mx-1 transition-colors duration-200
            ${
              currentPage === i
                ? "bg-blue-600 text-white shadow-md"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }
          `}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      pageNumbers.push(
        <span key="ellipsis-end" className="px-2">
          ...
        </span>
      );
    }

    return pageNumbers;
  };

  const renderModalContent = () => {
    if (modalAction === "delete") {
      return `Are you sure you want to delete the auto-responder "${selectedItem?.short_name}"? This will delete all associated sequences.`;
    }
    if (modalAction === "rename") {
      return (
        <form>
          <label className="block text-sm font-medium text-gray-700 mb-2 dark:bg-gray-800 dark:text-gray-200">
            New Title:
          </label>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="block w-full px-3 py-2 bg-blue-100 border border-gray-300 rounded-md shadow-sm dark:bg-gray-900 dark:text-gray-200"
          />
        </form>
      );
    }
    return null;
  };

  const getModalIconAndColor = () => {
    if (modalAction === "delete") {
      return {
        icon: (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        ),
        color: "red",
      };
    }
    return {
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
        </svg>
      ),
      color: "blue",
    };
  };

  const renderModalFooter = () => {
    const actionButton =
      modalAction === "delete" ? (
        <button
          onClick={handleAction}
          disabled={loading}
          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          {loading ? "Deleting..." : "Delete"}
        </button>
      ) : (
        <button
          onClick={handleAction}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700"
        >
          {loading ? "Saving..." : "Update"}
        </button>
      );
    return <>{actionButton}</>;
  };

  return (
    <PageContent title="Investor Leads" subtitle="Autoresponders" error={error}>
      <div className="p-8 space-y-8">
        {/* Hero Banner */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-lg">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3.196 12.87c-.263.13-.597-.04-.686-.3a.5.5 0 00-.493-.39H1.5a.5.5 0 00-.5.5v.5c0 .278.224.5.5.5h.327c.077-.26.22-.5.414-.696M16.5 1H3.5A2.5 2.5 0 001 3.5v13A2.5 2.5 0 003.5 19h13a2.5 2.5 0 002.5-2.5v-13A2.5 2.5 0 0016.5 1zm-5 16.5h-3v-5h3v5zm5-7.5a.5.5 0 01-.5.5H4a.5.5 0 01-.5-.5v-5a.5.5 0 01.5-.5h12a.5.5 0 01.5.5v5z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold">Autoresponder Sequences</h2>
                <p className="text-blue-100">Create and manage automated email campaigns</p>
              </div>
            </div>
            <div className="text-right text-blue-100 text-sm">
              <div className="font-semibold">{responders?.length || 0} Total Sequences</div>
            </div>
          </div>
        </div>

        {/* Table Card */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="p-6 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-50 dark:bg-gray-700/50 rounded-t-xl border-b border-gray-200 dark:border-gray-700">
            <div className="relative w-full sm:w-3/4">
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search autoresponders..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="block w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors"
              />
            </div>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="w-full sm:w-auto px-5 py-2 text-sm font-semibold rounded-lg text-white bg-blue-600 hover:bg-blue-700 dark:hover:bg-blue-500 transition-colors flex-shrink-0 whitespace-nowrap"
            >
              + Create Sequence
            </button>
          </div>
          <div className="overflow-y-visible">
            <table className="w-full text-sm text-left">
              <thead className="text-xs font-semibold text-gray-700 uppercase bg-gray-50 dark:text-gray-300 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 tracking-wider">
              <tr>
                <th className="px-6 py-4 text-gray-600 dark:text-gray-400">Autoresponder</th>
                <th className="px-6 py-4 text-gray-600 dark:text-gray-400">Email Sequences</th>
                <th className="px-6 py-4 text-center text-gray-600 dark:text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.length > 0 ? (
                currentItems.map((item, index) => (
                  <tr
                    key={item.short_name}
                    className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <td
                      className="px-6 py-4 font-medium text-gray-900 dark:text-gray-200 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                      onClick={() =>
                        onViewChange(
                         "investorrespondersview",
                          "investorresponders",
                          { shortname: item.short_name }
                        )
                      }
                    >
                      {item.short_name}
                    </td>
                    <td
                      className="px-6 py-4 text-gray-600 dark:text-gray-400 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400"
                      onClick={() =>
                        onViewChange(
                          "investorrespondersview",
                          "investorresponders",
                          { shortname: item.short_name }
                        )
                      }
                    >
                      <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                        {item.number} sequences
                      </span>
                    </td>
                    <td
                      className="px-6 py-4 text-center relative"
                      ref={(el) => (menuRef.current[index] = el)}
                    >
                      <button
                        onClick={() =>
                          setMenuOpenFor(
                            menuOpenFor === item.short_name
                              ? null
                              : item.short_name
                          )
                        }
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                      >
                                               <svg
                          className="w-5 h-5 text-gray-500 dark:text-gray-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                        </svg>
                      </button>
                      {menuOpenFor === item.short_name && (
                        <div className="absolute right-0 mt-2 w-48 bg-white border rounded shadow-lg z-10 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900">
                          <button
                            onClick={() => openModal("rename", item)}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                          >
                            <svg
                              className="w-4 h-4 flex-shrink-0 inline mr-2"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                            </svg>
                            Rename
                          </button>
                          <div className="border-t border-gray-200 dark:border-gray-700">
                            <button
                              onClick={() => openModal("delete", item)}
                              className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            >
                              <svg
                                className="w-4 h-4 flex-shrink-0 inline mr-2"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="3"
                    className="px-6 py-12 text-center"
                  >
                    <div className="flex flex-col items-center justify-center">
                      <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-gray-500 dark:text-gray-400 font-medium">No autoresponder sequences found</p>
                      <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">Create a new sequence to get started</p>
                    </div>
                  </td>
                </tr>
              )}
              </tbody>
            </table>
          </div>
          {filteredResponders.length > itemsPerPage && (
            <div className="p-6 flex items-center justify-between gap-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 rounded-b-xl">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                ← Previous
              </button>
              <div className="flex-grow flex justify-center gap-1">
                {renderPageNumbers()}
              </div>
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </div>
      <Modal
        show={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={
          modalAction === "delete"
            ? "Delete Autoresponder"
            : "Rename Autoresponder"
        }
        footer={renderModalFooter()}
        icon={getModalIconAndColor().icon}
        iconColor={getModalIconAndColor().color}
      >
        {renderModalContent()}
      </Modal>
      <SequenceModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={pullResponderData}
        user={user}
        type="3"
        isNew={true}
        setError={setError}
      />
    </PageContent>
  );
};

export default InvestorResponders;
